/** Formats / redacts GenerationRequestLog payloads for UI (poll groups, base64 mask, media URLs). */

export type WireExchange = {
  kind?: string
  verb?: string
  url?: string
  task_status?: string
  first_response?: unknown
  repeat_count?: number
  last_polled_at?: string
  request?: unknown
  response?: unknown
}

export type ResponsePayload = {
  exchanges?: WireExchange[]
  [key: string]: unknown
}

export type MediaPreviewKind = "image" | "video" | "audio"

const INLINE_DATA_KEYS = new Set(["inlineData", "inline_data"])
const BYTES_B64_KEYS = new Set(["bytesBase64Encoded", "bytes_base64_encoded"])
const NAMED_B64_KEYS = new Set([
  "image_base64",
  "video_base64",
  "audio_base64",
  "audio_base_64",
  "reference_audio_base64",
  "b64_json",
  "base64",
])
const BYTES_B64_MIN_REDACT = 64
const DATA_URI_RE = /^data:(image|video|audio)\/[^;]+;base64,/i
const URL_PREFIX_RE = /^(https?:|asset:)/i

const MEDIA_EXT_RE = /\.(png|jpe?g|webp|gif|bmp|svg|mp4|webm|mov|m4v)(?:\?|#|$)/i
const VIDEO_EXT_RE = /\.(mp4|webm|mov|m4v)(?:\?|#|$)/i
const STORAGE_PATH_RE = /\/(rails\/active_storage|blobs|disk|proxy)\b/i
const TOS_HOST_RE = /(^|\.)tos-s3-[^.]+\.bytepluses\.com$/i
const MEDIA_SLOT_HINT: Record<string, MediaPreviewKind> = {
  image_url: "image",
  video_url: "video",
  audio_url: "audio",
}

export function mediaPreviewHintForKey(key: string, current?: MediaPreviewKind): MediaPreviewKind | undefined {
  return MEDIA_SLOT_HINT[key] ?? current
}

function urlLike(s: string): boolean {
  return URL_PREFIX_RE.test(s)
}

function isDataUri(s: string): boolean {
  return DATA_URI_RE.test(s)
}

function stripDataUriPrefix(s: string): string {
  const idx = s.indexOf("base64,")
  return idx >= 0 ? s.slice(idx + "base64,".length) : s
}

function approxDecodedBytes(s: string): number {
  const raw = stripDataUriPrefix(s).replace(/\s+/g, "")
  if (!raw) return 0
  return Math.floor((raw.length * 3) / 4)
}

function omissionLabel(s: string): string {
  return `[omitted, ${approxDecodedBytes(s)} bytes]`
}

function omissionDataUriLabel(s: string): string {
  return `[omitted data-URI, ${approxDecodedBytes(s)} bytes]`
}

function redactableBinaryString(s: string): boolean {
  if (!s || urlLike(s)) return false
  if (isDataUri(s)) return true
  return s.length > BYTES_B64_MIN_REDACT
}

function redactStringValue(s: string): string {
  return isDataUri(s) ? omissionDataUriLabel(s) : s
}

function redactBinaryValue(s: string): string {
  return isDataUri(s) ? omissionDataUriLabel(s) : omissionLabel(s)
}

/** Mirror of backend WireLogBinaryRedactor — never touches http(s)/asset URLs. */
export function redactGenerationLogPayload(value: unknown): unknown {
  if (value == null) return value
  if (typeof value === "string") return redactStringValue(value)
  if (Array.isArray(value)) return value.map((item) => redactGenerationLogPayload(item))
  if (typeof value !== "object") return value

  const obj = value as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const [key, raw] of Object.entries(obj)) {
    if (INLINE_DATA_KEYS.has(key) && raw && typeof raw === "object" && !Array.isArray(raw)) {
      const nested = raw as Record<string, unknown>
      const nestedOut: Record<string, unknown> = {}
      for (const [nk, nv] of Object.entries(nested)) {
        if (nk === "data" && typeof nv === "string" && nv && !urlLike(nv)) {
          nestedOut[nk] = redactBinaryValue(nv)
        } else {
          nestedOut[nk] = redactGenerationLogPayload(nv)
        }
      }
      out[key] = nestedOut
    } else if (
      (BYTES_B64_KEYS.has(key) || NAMED_B64_KEYS.has(key) || key === "data") &&
      typeof raw === "string" &&
      redactableBinaryString(raw)
    ) {
      out[key] = redactBinaryValue(raw)
    } else {
      out[key] = redactGenerationLogPayload(raw)
    }
  }
  return out
}

/** Detect http(s) media URL suitable for lightbox preview. */
export function detectMediaPreviewUrl(
  value: string,
  hint?: MediaPreviewKind,
): { url: string; kind: MediaPreviewKind } | null {
  const s = value.trim()
  if (!/^https?:\/\//i.test(s)) return null
  if (isDataUri(s)) return null

  let pathname = s
  let host = ""
  try {
    const parsed = new URL(s)
    pathname = parsed.pathname
    host = parsed.hostname
  } catch {
    // keep raw
  }

  const isVideo = VIDEO_EXT_RE.test(pathname) || VIDEO_EXT_RE.test(s)
  const isImageExt = MEDIA_EXT_RE.test(pathname) || MEDIA_EXT_RE.test(s)
  const isStorage = STORAGE_PATH_RE.test(pathname) || STORAGE_PATH_RE.test(s)
  const isTos = TOS_HOST_RE.test(host)

  if (!isVideo && !isImageExt && !isStorage && !isTos) return null
  if (isTos) return { url: s, kind: hint ?? "image" }
  return { url: s, kind: isVideo ? "video" : "image" }
}

function formatPollExchange(ex: WireExchange, index: number): string {
  const polls = 1 + (ex.repeat_count ?? 0)
  const status = ex.task_status || "—"
  const last = ex.last_polled_at || "—"
  const first = redactGenerationLogPayload(ex.first_response ?? null)
  const lines = [
    `[${index + 1}] POLL ${ex.verb || "GET"} ${ex.url || ""}`,
    `  status: ${status}`,
    `  polls: ×${polls} (first + ${ex.repeat_count ?? 0} repeats)`,
    `  last_polled_at: ${last}`,
    `  first_response:`,
    JSON.stringify(first, null, 2)
      .split("\n")
      .map((line) => `    ${line}`)
      .join("\n"),
  ]
  return lines.join("\n")
}

function formatPlainExchange(ex: WireExchange, index: number): string {
  const lines = [`[${index + 1}] ${ex.verb || "?"} ${ex.url || ""}`]
  if (ex.request != null) {
    lines.push("  request:")
    lines.push(
      JSON.stringify(redactGenerationLogPayload(ex.request), null, 2)
        .split("\n")
        .map((line) => `    ${line}`)
        .join("\n"),
    )
  }
  if (ex.response != null) {
    lines.push("  response:")
    lines.push(
      JSON.stringify(redactGenerationLogPayload(ex.response), null, 2)
        .split("\n")
        .map((line) => `    ${line}`)
        .join("\n"),
    )
  }
  return lines.join("\n")
}

/** Pretty-print response_payload; poll groups get human-readable summary. */
export function formatGenerationResponsePayload(payload: unknown): string {
  if (payload == null) return ""
  if (typeof payload !== "object") return String(payload)

  const redacted = redactGenerationLogPayload(payload) as ResponsePayload
  const exchanges = redacted.exchanges
  if (!Array.isArray(exchanges) || exchanges.length === 0) {
    return JSON.stringify(redacted, null, 2)
  }

  const blocks = exchanges.map((ex, i) => {
    if (ex && typeof ex === "object" && ex.kind === "poll") {
      return formatPollExchange(ex, i)
    }
    return formatPlainExchange(ex as WireExchange, i)
  })

  return blocks.join("\n\n")
}
